import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
// import { useGetPermissionsQuery } from '../services/edgeApi';
import { MoonIcon, SunIcon, UserIcon } from '@iconicicons/react';
import { useViewMode } from '../hooks/useViewMode';
import { DevelopmentFlag } from '../helpers/DevelopmentFlag';
import { useTheme } from '../helpers/hooks';
import { Can } from '../user/Can';
import { useSession } from '../hooks/useSession';

type NavbarButton = {
  url: string;
  name: string;
  permissions: string | string[];
  subemnus?: NavbarButton[];
  dev?: boolean;
};

const navbarButtons: NavbarButton[] = [
  // just using point right now as the defacto permission model
  { url: '/', name: 'Map', permissions: 'view_point' },
  {
    url: '/market-conditions',
    name: 'Market Conditions',
    permissions: ['view_flow'],
  },
  {
    url: '/dashboards',
    name: 'Dashboards',
    permissions: ['view_flow'],
  },
  {
    url: '/scenarios/next_day/path-report',
    name: 'Paths',
    permissions: ['view_pathrecord'],
  },
  {
    url: '/scenarios',
    name: 'Scenarios',
    permissions: 'view_scenario',
    subemnus: [
      {
        url: '/scenarios/next_day',
        name: 'Next Day',
        permissions: 'view_scenario',
      },
      {
        url: '/scenarios/new',
        name: 'Custom Scenario',
        permissions: 'add_scenario',
      },
    ],
  },
  {
    url: '/notices',
    name: 'Notices',
    permissions: 'view_notices',
  },
  { url: '/contracts', name: 'Contracts', permissions: 'view_contract' },
  {
    url: '/katana-explorer',
    name: 'Katana Explorer',
    permissions: ['view_pathrecord'],
  },
  {
    url: '/index-of-customers',
    name: 'Index of Customers',
    permissions: 'view_ioccontract',
  },
  { url: '/segmentation', name: 'Segmentation', permissions: 'view_point' },

  // {
  //   url: '/scenario-groups',
  //   name: 'Scenario Groups',
  //   permissions: 'view_point',
  // },

  //{ url: '/day-over-day', name: 'Day Over Day', permissions: 'view_point' },
  // {url: "/all-paths", name:"All Paths"},
  // {url: "/all-receipts", name:"All Receipts to Delivery Point"},
  // {url: "/segmentation", name:"Max Segmentation"},
  // {url: "/visualize-data", name:"Visualize Data"},
  // {url: "/optimize", name:"Optimize Assets"},
  {
    url: '/map-builder',
    name: 'Map Builder',
    permissions: [
      'change_point',
      'change_edge',
      'change_interconnect',
      'change_node',
    ],
  },
];

export type NavBarProps = {
  navContent: React.ReactNode;
};

export const NavBar: React.FC<NavBarProps> = ({ navContent }) => {
  const navigate = useNavigate();
  // const isAuthenticated = Boolean(localStorage.getItem('token'));
  // const { data: sessionData = {} } = useGetPermissionsQuery(undefined, {
  //   skip: !isAuthenticated,
  // });
  // const { info: user } = sessionData;
  const { user, isAuthenticated } = useSession();
  const isProduction = process.env.NODE_ENV === 'production';
  const [viewMode, setViewMode] = useViewMode();
  const [theme, setTheme] = useTheme();

  const logOut = () => {
    localStorage.removeItem('token');
    window.dispatchEvent(
      new StorageEvent('local-storage', { key: 'token', newValue: undefined }),
    );
    navigate('/login');
  };

  const accountSettings = () => {
    navigate('/account');
  };

  return (
    <nav className="navbar navbar-light bg-light d-flex" id="app-navbar">
      <a
        className="navbar-brand"
        href="/"
        target="_blank"
        rel="noreferrer noopener"
      >
        <img
          style={{
            paddingLeft: '10px',
          }}
          src="/logo.png"
          height="50"
          alt=""
        />
      </a>

      {localStorage.getItem('token') && (
        <>
          {isAuthenticated && (
            <div
              className="d-flex justify-content-between mx-3 h-100"
              style={{ flexGrow: 1, flexShrink: 0 }}
            >
              <div className="navbar navbar-expand-lg navbar-light bg-light gap-4 h-100 py-0">
                {navbarButtons
                  .filter((button) => viewMode === 'development' || !button.dev)
                  .map((button) => (
                    <Can key={button.url} can={button.permissions}>
                      <div className="hover-dropdown h-100" key={button.url}>
                        <NavLink
                          to={button.url}
                          className={({ isActive }) =>
                            `nav-item nav-link hover-dropdown-trigger h-100 align-items-center d-flex ${
                              isActive ? 'active text-primary' : ''
                            }`
                          }
                        >
                          {button.name}
                        </NavLink>
                        <div className="hover-dropdown-content">
                          <div className="mt-2">
                            {button.subemnus
                              ?.filter(
                                (button) =>
                                  viewMode === 'development' || !button.dev,
                              )
                              .map((submenu) => (
                                <NavLink
                                  key={submenu.url}
                                  to={submenu.url}
                                  className={() => `dropdown-item`}
                                >
                                  <span className="ps-2 pe-5 py-2 d-flex align-items-center fs-6">
                                    {submenu.dev && (
                                      <DevelopmentFlag className="me-2" />
                                    )}
                                    {submenu.name}
                                  </span>
                                </NavLink>
                              ))}
                          </div>
                        </div>
                      </div>
                    </Can>
                  ))}
              </div>
            </div>
          )}
          {navContent}
          <div className="d-flex">
            <div className="d-flex gap-4">
              {!isProduction && (
                <div className="d-flex gap-1">
                  <div className="form-switch justify-content-center d-flex align-items-center">
                    <input
                      role="button"
                      className="form-check-input"
                      type="checkbox"
                      id="mode-switch"
                      checked={
                        { development: true, production: false }[viewMode]
                      }
                      onChange={(evt) =>
                        setViewMode(
                          evt.target.checked ? 'development' : 'production',
                        )
                      }
                    />
                  </div>
                  <label
                    role="button"
                    className="form-check-label d-flex align-items-center"
                    onClick={() => setViewMode('development')}
                  >
                    <DevelopmentFlag />
                  </label>
                </div>
              )}
              <div className="d-flex gap-1">
                <label
                  role="button"
                  className="form-check-label d-flex align-items-center"
                  onClick={() => setTheme('dark')}
                >
                  <MoonIcon />
                </label>
                <div className="form-switch justify-content-center d-flex align-items-center">
                  <input
                    role="button"
                    className="form-check-input"
                    type="checkbox"
                    id="theme-switch"
                    checked={{ light: true, dark: false }[theme]}
                    onChange={(evt) =>
                      setTheme(evt.target.checked ? 'light' : 'dark')
                    }
                  />
                </div>
                <label
                  role="button"
                  className="form-check-label d-flex align-items-center"
                  onClick={() => setTheme('light')}
                >
                  <SunIcon />
                </label>
              </div>
              {/* <Button
                color="outline-primary"
                className="mx-2"
                onClick={() => dispatch(overlaysActions.setModal(true))}
              >
                Set Map Overlays
              </Button> */}
            </div>
            <div className="dropdown mx-2">
              <button
                className="btn btn-outline dropdown-toggle d-flex align-items-center"
                type="button"
                id="account-menu-button"
                data-bs-toggle="dropdown"
                // aria-expanded="false"
              >
                {user?.first_name ?? user?.email} <UserIcon className="ms-1" />
              </button>
              <ul
                className="dropdown-menu dropdown-menu-end"
                aria-labelledby="account-menu-button"
              >
                <li>
                  <a
                    className="dropdown-item"
                    type="button"
                    onClick={accountSettings}
                  >
                    Account
                  </a>
                </li>
                <li>
                  <a className="dropdown-item" type="button" onClick={logOut}>
                    Log Out
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </>
      )}
    </nav>
  );
};
